import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { list } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Progress } from "@/components/ui/progress";
import { fmtIDR, fmtDate, daysUntil } from "@/lib/constants";
import { StatusBadge } from "@/components/StatusBadge";
import { Gem, Users, Building2, Wallet, CalendarDays, TrendingUp, Clock, MapPin, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import MiniCalendar from "@/components/MiniCalendar";

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="card-lux hover-lift p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-[#7A7A7A]">{label}</p>
        <p className="font-heading text-3xl text-[#1A1A1A] mt-1.5 leading-none">{value}</p>
      </div>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent}`}>
        <Icon className="w-5 h-5" strokeWidth={1.6} />
      </div>
    </div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/dashboard/upcoming").then((r) => setUpcoming(r.data)).catch(() => {});
    list("activity_logs").then((d) => setLogs(d.slice(-8).reverse())).catch(() => {});
  }, []);

  return (
    <div>
      <PageHeader overline="Ringkasan Operasional" title={`Halo, ${user?.name?.split(" ")[0] || ""}`} subtitle="Pantau semua event, klien, dan keuangan Anda dalam satu tempat." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6 stagger">
        <StatCard icon={Gem} label="Total Event" value={stats?.total_event ?? "-"} accent="bg-burgundy/10 text-burgundy" />
        <StatCard icon={CalendarDays} label="Event Bulan Ini" value={stats?.event_this_month ?? "-"} accent="bg-blue-100 text-blue-600" />
        <StatCard icon={Clock} label="Event Mendatang" value={stats?.event_upcoming ?? "-"} accent="bg-gold/20 text-gold-dark" />
        <StatCard icon={TrendingUp} label="Event Selesai" value={stats?.event_completed ?? "-"} accent="bg-emerald-100 text-emerald-600" />
        <StatCard icon={Users} label="Total Client" value={stats?.total_client ?? "-"} accent="bg-violet-100 text-violet-600" />
        <StatCard icon={Building2} label="Total Vendor" value={stats?.total_vendor ?? "-"} accent="bg-blue-100 text-blue-600" />
        <StatCard icon={Wallet} label="Outstanding" value={stats ? fmtIDR(stats.outstanding_payment) : "-"} accent="bg-red-100 text-red-600" />
        <StatCard icon={TrendingUp} label="Revenue Bulan Ini" value={stats ? fmtIDR(stats.revenue_this_month) : "-"} accent="bg-emerald-100 text-emerald-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div>
            <h2 className="font-heading text-2xl text-burgundy mb-3">Upcoming Wedding</h2>
            <div className="space-y-4 stagger">
              {upcoming.length === 0 && <div className="card-lux p-8 text-center text-[#7A7A7A]">Belum ada event mendatang.</div>}
              {upcoming.map((e) => {
                const cd = e.countdown_days ?? daysUntil(e.wedding_date);
                return (
                  <Link to={`/events/${e.id}`} key={e.id} className="block card-lux hover-lift p-5" data-testid="upcoming-event-card">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold-dark">The Wedding of</p>
                          <StatusBadge status={e.status} />
                        </div>
                        <h3 className="font-heading text-2xl text-[#1A1A1A]">{e.groom} & {e.bride}</h3>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-[#7A7A7A]">
                          <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" />{fmtDate(e.wedding_date)}</span>
                          <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{e.venue}</span>
                          <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" />{e.pic}</span>
                        </div>
                        <div className="mt-3">
                          <div className="flex justify-between text-xs mb-1"><span className="text-[#7A7A7A]">Progress</span><span className="font-semibold text-burgundy">{e.progress}%</span></div>
                          <Progress value={e.progress} className="h-1.5 bg-beige" />
                        </div>
                      </div>
                      <div className="sm:text-center sm:pl-6 sm:border-l border-gold/20">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-[#7A7A7A]">Countdown</p>
                        <p className="font-heading text-4xl text-burgundy leading-none mt-1">{cd < 0 ? 0 : cd}</p>
                        <p className="text-xs text-[#7A7A7A]">days</p>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <h2 className="font-heading text-2xl text-burgundy mb-3">Kalender</h2>
            <MiniCalendar />
          </div>
          <div>
            <h2 className="font-heading text-2xl text-burgundy mb-3">Aktivitas Terbaru</h2>
            <div className="card-lux p-4 space-y-3">
              {logs.length === 0 && <p className="text-sm text-[#7A7A7A] text-center py-4">Belum ada aktivitas.</p>}
              {logs.map((l) => (
                <div key={l.id} className="flex gap-3 text-sm" data-testid="activity-log-item">
                  <span className="w-1.5 h-1.5 rounded-full bg-gold mt-1.5 shrink-0" />
                  <div>
                    <p className="text-[#4A4A4A]"><span className="font-semibold text-[#1A1A1A]">{l.user_name}</span> {l.action} {l.entity !== "seed" ? l.entity : ""} {l.detail && `— ${l.detail}`}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
